/**
 * VersionHistoryPanel — Google Drive-style version timeline.
 *
 * Reads version data directly from MinIO (via API). Each version has:
 * version_id, version_number, file_size, last_modified, is_current.
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';

import { formatDateTime, formatNumber } from '@core/i18n/format';

const VERSION_DATE_FORMAT = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };

function formatSize(bytes, t, locale) {
  const size = (value) => formatNumber(value, locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  if (bytes < 1024) return t('dataroom.versionHistory.sizeBytes', { size: formatNumber(bytes, locale) });
  if (bytes < 1024 * 1024) return t('dataroom.versionHistory.sizeKb', { size: size(bytes / 1024) });
  return t('dataroom.versionHistory.sizeMb', { size: size(bytes / (1024 * 1024)) });
}

export function VersionHistoryPanel({
  versions = [],
  activeVersionUuid,
  canRestore = true,
  onPreview,
  onDiff,
  onDownload,
  onRestore,
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const [restoreTarget, setRestoreTarget] = useState(null);

  if (!versions.length) {
    return (
      <div className="p-4 text-sm text-[var(--color-text-muted)]">
        {t('dataroom.versionHistory.empty')}
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-0 overflow-y-auto">
        {versions.map((version) => {
          const isCurrent = version.is_current;
          const isActive = activeVersionUuid === version.version_id;

          return (
            <div
              key={version.version_id}
              className={`border-b border-[var(--color-border)] p-3 last:border-b-0 transition-colors ${
                isActive
                  ? 'bg-[var(--color-accent-subtle)] border-l-2 border-l-[var(--color-accent)]'
                  : ''
              }`}
            >
              {/* Header */}
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-[var(--color-text)]">
                  {isCurrent
                    ? t('dataroom.versionHistory.versionCurrent', { number: version.version_number })
                    : t('dataroom.versionHistory.version', { number: version.version_number })}
                </span>
                {isActive && (
                  <span className="text-xs text-[var(--color-accent)] font-medium">
                    {t('dataroom.versionHistory.viewing')}
                  </span>
                )}
              </div>

              {/* Metadata */}
              <div className="text-xs text-[var(--color-text-muted)] mb-2">
                {formatDateTime(version.last_modified, locale, VERSION_DATE_FORMAT)} · {formatSize(version.file_size, t, locale)}
              </div>

              {/* Actions */}
              <div className="flex gap-1.5">
                <button
                  type="button"
                  className="text-xs px-2 py-1 rounded border border-[var(--color-border)] hover:bg-[var(--color-accent-subtle)] text-[var(--color-text-secondary)]"
                  onClick={() => onPreview?.(version)}
                >
                  {t('dataroom.versionHistory.preview')}
                </button>
                {!isCurrent && (
                  <button
                    type="button"
                    className="text-xs px-2 py-1 rounded border border-[var(--color-border)] hover:bg-[var(--color-accent-subtle)] text-[var(--color-text-secondary)]"
                    onClick={() => onDiff?.(version)}
                  >
                    {t('dataroom.versionHistory.diff')}
                  </button>
                )}
                <button
                  type="button"
                  aria-label={t('dataroom.versionHistory.download')}
                  className="text-xs px-2 py-1 rounded border border-[var(--color-border)] hover:bg-[var(--color-accent-subtle)] text-[var(--color-text-secondary)]"
                  onClick={() => onDownload?.(version)}
                >
                  <span aria-hidden="true">↓</span>
                </button>
                {/* Restoring writes a new version, so guests never see it. */}
                {!isCurrent && canRestore && (
                  <button
                    type="button"
                    aria-label={t('dataroom.versionHistory.restoreVersion')}
                    className="text-xs px-2 py-1 rounded border border-amber-300 hover:bg-amber-50 text-amber-600"
                    onClick={() => setRestoreTarget(version)}
                  >
                    <span aria-hidden="true">↩</span>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {restoreTarget && canRestore && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          onClick={() => setRestoreTarget(null)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50" />
          {/* Modal */}
          <div
            className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="flex justify-center mb-4">
                <span className="inline-flex justify-center items-center size-12 rounded-full bg-amber-50 text-amber-600">
                  <Icon icon="mdi:alert-outline" className="size-6" />
                </span>
              </div>
              <h3 className="text-lg font-semibold text-[var(--color-text)]">{t('dataroom.versionHistory.restoreVersion')}</h3>
              <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
                {t('dataroom.versionHistory.restoreMessage', { number: restoreTarget.version_number })}
              </p>
            </div>
            <div className="flex justify-end items-center gap-2 py-3 px-4 border-t border-[var(--color-border)]">
              <button
                type="button"
                onClick={() => setRestoreTarget(null)}
                className="cursor-pointer py-2 px-3 text-sm font-medium rounded-lg border border-[var(--color-border)] bg-white text-[var(--color-text)] hover:bg-[var(--color-bg-muted)]"
              >
                {t('dataroom.versionHistory.cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  onRestore?.(restoreTarget);
                  setRestoreTarget(null);
                }}
                className="cursor-pointer py-2 px-3 text-sm font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700"
              >
                {t('dataroom.versionHistory.restore')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
